/**
 * Sync Engine (Day-15 Module 2)
 * 
 * Handles synchronization of offline actions with the server
 * - Idempotent sync using action.id (offlineId)
 * - Auto-retry on reconnect
 * - Conflict resolution
 */

import api from '../api';
import {
  getAllQueuedActions,
  updateActionStatus,
  markActionSynced,
  markActionFailed,
  ACTION_STATUS,
  ACTION_TYPES,
} from './actionQueue';
import { STORES, updateInStore, getFromStore } from './storage';

// Sync state
let isSyncing = false;
let syncListeners = [];

// ─────────────────────────────────────────────────────────────────
// Sync Status Management
// ─────────────────────────────────────────────────────────────────

/**
 * Register a sync listener
 */
export function onSyncStatusChange(callback) {
  syncListeners.push(callback);
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback);
  };
}

/**
 * Notify all listeners of sync status change
 */
function notifySyncStatus(status) {
  syncListeners.forEach(cb => cb(status));
  window.dispatchEvent(new CustomEvent('syncStatusChanged', { detail: status }));
}

/**
 * Check if online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Check if currently syncing
 */
export function getSyncingStatus() {
  return isSyncing;
}

// ─────────────────────────────────────────────────────────────────
// API Endpoint Mapping
// ─────────────────────────────────────────────────────────────────

const ACTION_ENDPOINTS = {
  [ACTION_TYPES.CHECKIN]: {
    method: 'POST',
    getUrl: (action) => '/mobile/shifts/check-in',
    getBody: (action) => ({
      offlineId: action.id,
      shiftId: action.payload.shiftId,
      timestamp: action.payload.timestamp || action.createdAt,
      lat: action.payload.lat,
      lng: action.payload.lng,
      notes: action.payload.notes,
    }),
  },
  [ACTION_TYPES.CHECKOUT]: {
    method: 'POST',
    getUrl: () => '/mobile/shifts/check-out',
    getBody: (action) => ({
      offlineId: action.id,
      shiftId: action.payload.shiftId,
      timestamp: action.payload.timestamp || action.createdAt,
      lat: action.payload.lat,
      lng: action.payload.lng,
      notes: action.payload.notes,
    }),
  },
  [ACTION_TYPES.PROGRESS_NOTE]: {
    method: 'POST',
    getUrl: () => '/mobile/progress-notes',
    getBody: (action) => ({
      offlineId: action.id,
      participantId: action.payload.participantId,
      shiftId: action.payload.shiftId,
      timestamp: action.payload.timestamp || action.createdAt,
      noteType: action.payload.noteType || 'GENERAL',
      content: action.payload.content,
      mood: action.payload.mood,
      activities: action.payload.activities,
      attachmentIds: action.payload.attachmentIds,
    }),
  },
  [ACTION_TYPES.INCIDENT_REPORT]: {
    method: 'POST',
    getUrl: () => '/mobile/incidents',
    getBody: (action) => ({
      offlineId: action.id,
      participantId: action.payload.participantId,
      shiftId: action.payload.shiftId,
      timestamp: action.payload.timestamp || action.createdAt,
      incidentType: action.payload.incidentType,
      severity: action.payload.severity,
      description: action.payload.description,
      location: action.payload.location,
      immediateActions: action.payload.immediateActions,
      witnesses: action.payload.witnesses,
      attachmentIds: action.payload.attachmentIds,
    }),
  },
  [ACTION_TYPES.MEDICATION_LOG]: {
    method: 'POST',
    getUrl: () => '/mobile/emar/administer',
    getBody: (action) => ({
      offlineId: action.id,
      scheduleId: action.payload.scheduleId,
      timestamp: action.payload.timestamp || action.createdAt,
      status: action.payload.status,
      notes: action.payload.notes,
      witnessId: action.payload.witnessId,
    }),
  },
  [ACTION_TYPES.TASK_COMPLETE]: {
    method: 'POST',
    getUrl: () => '/mobile/tasks/complete',
    getBody: (action) => ({
      offlineId: action.id,
      taskId: action.payload.taskId,
      timestamp: action.payload.timestamp || action.createdAt,
      notes: action.payload.notes,
      attachmentIds: action.payload.attachmentIds,
    }),
  },
  [ACTION_TYPES.HANDOVER_NOTE]: {
    method: 'POST',
    getUrl: () => '/mobile/handovers',
    getBody: (action) => ({
      offlineId: action.id,
      shiftId: action.payload.shiftId,
      timestamp: action.payload.timestamp || action.createdAt,
      content: action.payload.content,
      priority: action.payload.priority,
      participantIds: action.payload.participantIds,
    }),
  },
  [ACTION_TYPES.ATTACHMENT_UPLOAD]: {
    method: 'POST',
    getUrl: () => '/mobile/attachments',
    isMultipart: true,
    getBody: (action) => {
      const formData = new FormData();
      formData.append('offlineId', action.id);
      formData.append('file', action.payload.file);
      formData.append('entityType', action.payload.entityType);
      formData.append('entityId', action.payload.entityId);
      return formData;
    },
  },
};

// ─────────────────────────────────────────────────────────────────
// Sync Operations
// ─────────────────────────────────────────────────────────────────

/**
 * Sync a single action
 */
async function syncAction(action) {
  const endpoint = ACTION_ENDPOINTS[action.actionType];
  
  if (!endpoint) {
    console.warn(`No endpoint configured for action type: ${action.actionType}`);
    await markActionFailed(action.id, `Unknown action type: ${action.actionType}`);
    return { success: false, error: 'Unknown action type' };
  }

  try {
    const url = endpoint.getUrl(action);
    const body = endpoint.getBody(action);

    let response;
    const config = endpoint.isMultipart ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};

    if (endpoint.method === 'POST') {
      response = await api.post(url, body, config);
    } else if (endpoint.method === 'PUT') {
      response = await api.put(url, body, config);
    } else if (endpoint.method === 'PATCH') {
      response = await api.patch(url, body, config);
    }

    // Check for ALREADY_SYNCED response (idempotency)
    const data = response.data?.data || response.data;
    if (data?.status === 'ALREADY_SYNCED') {
      await markActionSynced(action.id, data.existingId || data.id);
      return { success: true, serverId: data.existingId || data.id, duplicate: true };
    }

    // Get server ID from response
    const serverId = data?.id;
    await markActionSynced(action.id, serverId);
    
    return { success: true, serverId };
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    
    // Check for conflict (409)
    if (error.response?.status === 409) {
      const existingId = error.response?.data?.data?.existingId;
      if (existingId) {
        // Duplicate - mark as synced
        await markActionSynced(action.id, existingId);
        return { success: true, serverId: existingId, duplicate: true };
      }
    }
    
    // Mark as failed
    await markActionFailed(action.id, errorMessage);
    
    // Determine if retryable
    const isRetryable = !error.response || 
                        error.response.status >= 500 || 
                        error.response.status === 401;
    
    return { success: false, error: errorMessage, retryable: isRetryable };
  }
}

/**
 * Extract error message from API error
 */
function getErrorMessage(error) {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.detail) {
    return typeof error.response.data.detail === 'string' 
      ? error.response.data.detail 
      : JSON.stringify(error.response.data.detail);
  }
  if (error.message) {
    return error.message;
  }
  return 'Unknown error occurred';
}

/**
 * Sync all pending actions using batch endpoint
 */
export async function syncAllActions() {
  if (isSyncing) {
    console.log('Sync already in progress');
    return { skipped: true };
  }

  if (!isOnline()) {
    console.log('Offline - skipping sync');
    return { offline: true };
  }

  isSyncing = true;
  notifySyncStatus({ syncing: true, progress: 0 });

  const results = {
    total: 0,
    synced: 0,
    failed: 0,
    duplicates: 0,
    conflicts: 0,
  };

  try {
    const actions = await getAllQueuedActions();
    
    // Get pending and retryable failed actions (max 5 retries)
    const pending = actions.filter(
      a => a.status === ACTION_STATUS.PENDING || 
           (a.status === ACTION_STATUS.FAILED && (a.retryCount || 0) < 5)
    );

    results.total = pending.length;

    if (pending.length === 0) {
      notifySyncStatus({ syncing: false, lastSync: new Date().toISOString(), results });
      return results;
    }

    // Sort by createdAt to maintain order
    pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    // Prepare batch request
    const batchActions = pending.map(action => ({
      clientActionId: action.id,
      actionType: action.actionType,
      payload: action.payload,
      createdAt: action.createdAt,
    }));

    notifySyncStatus({
      syncing: true,
      progress: 50,
      current: 'Batch sync',
      totalCount: pending.length,
    });

    // Send batch sync request
    const response = await api.post('/mobile/sync', { actions: batchActions });
    const data = response.data;

    // Process successful actions
    for (const success of (data.successActions || [])) {
      const action = pending.find(a => a.id === success.clientActionId);
      if (action) {
        await markActionSynced(action.id, success.serverId);
        results.synced++;
      }
    }

    // Process failed actions
    for (const failed of (data.failedActions || [])) {
      const action = pending.find(a => a.id === failed.clientActionId);
      if (action) {
        await markActionFailed(action.id, failed.error?.message || 'Sync failed');
        results.failed++;
      }
    }

    // Process conflicts (duplicates)
    for (const conflict of (data.conflicts || [])) {
      const action = pending.find(a => a.id === conflict.clientActionId);
      if (action) {
        if (conflict.conflictType === 'DUPLICATE') {
          // Mark as synced since it already exists on server
          await markActionSynced(action.id, conflict.serverData?.serverId);
          results.duplicates++;
        } else {
          // Handle other conflicts as errors
          await markActionFailed(action.id, conflict.message);
          results.conflicts++;
        }
      }
    }

    const lastSync = new Date().toISOString();
    notifySyncStatus({ syncing: false, lastSync, results });
    
    // Store last sync time
    await updateInStore(STORES.SYNC_STATE, {
      key: 'lastSync',
      timestamp: lastSync,
      results,
    });

    return results;
  } catch (error) {
    console.error('Sync error:', error);
    
    // Fallback to single-action sync if batch fails
    if (error.response?.status === 422 || error.response?.status === 400) {
      console.log('Batch sync failed, falling back to single-action sync');
      return await syncAllActionsSingle();
    }
    
    notifySyncStatus({ syncing: false, error: error.message });
    return { error: error.message };
  } finally {
    isSyncing = false;
  }
}

/**
 * Fallback: Sync actions one by one (legacy method)
 */
async function syncAllActionsSingle() {
  const results = {
    total: 0,
    synced: 0,
    failed: 0,
    duplicates: 0,
  };

  try {
    const actions = await getAllQueuedActions();
    const pending = actions.filter(
      a => a.status === ACTION_STATUS.PENDING || 
           (a.status === ACTION_STATUS.FAILED && (a.retryCount || 0) < 5)
    );

    results.total = pending.length;
    pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    for (let i = 0; i < pending.length; i++) {
      const action = pending[i];
      
      notifySyncStatus({
        syncing: true,
        progress: Math.round(((i + 1) / pending.length) * 100),
        current: action.actionType,
        currentIndex: i + 1,
        totalCount: pending.length,
      });

      const result = await syncAction(action);
      
      if (result.success) {
        results.synced++;
        if (result.duplicate) results.duplicates++;
      } else {
        results.failed++;
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const lastSync = new Date().toISOString();
    notifySyncStatus({ syncing: false, lastSync, results });
    
    await updateInStore(STORES.SYNC_STATE, {
      key: 'lastSync',
      timestamp: lastSync,
      results,
    });

    return results;
  } catch (error) {
    console.error('Single sync error:', error);
    return { error: error.message };
  }
}

/**
 * Get last sync info
 */
export async function getLastSyncInfo() {
  return getFromStore(STORES.SYNC_STATE, 'lastSync');
}

/**
 * Setup automatic sync on reconnect
 */
export function setupAutoSync() {
  // Sync when coming online
  window.addEventListener('online', () => {
    console.log('Back online - starting sync');
    setTimeout(() => syncAllActions(), 1000);
  });

  // Periodic sync when online (every 5 minutes)
  setInterval(() => {
    if (isOnline() && !isSyncing) {
      syncAllActions();
    }
  }, 5 * 60 * 1000);

  // Initial sync if online (after 2 seconds)
  if (isOnline()) {
    setTimeout(() => syncAllActions(), 2000);
  }
}

// Export sync action for manual single sync
export { syncAction };
